import { UseQueryResult } from "@tanstack/react-query";
import { PropsWithChildren, createContext, useContext } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  ProviderByIdQuery,
  ProvidersQuery,
  useProviderByIdQuery,
  useProvidersQuery,
} from "@api/graphql/types-and-hooks";

interface ProvidersDataContextI {
  providersQuery: UseQueryResult<ProvidersQuery, unknown>;
  providerDetailsQuery: UseQueryResult<ProviderByIdQuery, unknown>;
}

const ProvidersDataContext = createContext<ProvidersDataContextI | undefined>(undefined);

export const useProvidersData = () => {
  const context = useContext(ProvidersDataContext);
  if (context === undefined) {
    throw new Error("useProvidersData must be used within a ProvidersDataProvider");
  }
  return context;
};

export const ProvidersDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const { providerId } = useParams();

  const providersQuery = useProvidersQuery(
    {},
    {
      queryKey: ["providers"],
      refetchOnWindowFocus: false,
      retry: false,
      enabled: pathname.includes("/members"),
    },
  );

  if (!providersQuery) return null;

  const providerDetailsQuery = useProviderByIdQuery(
    { providerId: providerId ? providerId : "" },
    {
      queryKey: ["provider-by-id", providerId],
      enabled: !!providerId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  return (
    <ProvidersDataContext.Provider
      value={{
        providersQuery: providersQuery,
        providerDetailsQuery: providerDetailsQuery,
      }}
    >
      {children}
    </ProvidersDataContext.Provider>
  );
};
